import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useResolvedNav } from 'config/navigation/useResolvedNav'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import useWindowWidth from 'utils/hooks/useWindowWidth'
import LocalStorage from 'utils/LocalStorage'
import { URL_CONST } from 'utils/urlConsts'

import { selectUserId } from 'containers/App/selectors'

import Drawer from 'components/lib/DesignSystem/V3/Drawer'
import SettingsNav from 'components/pages/navigation/sideNav/SettingsNav'
import SettingsPanelView from 'components/pages/navigation/sideNav/SettingsPanelView'
import SideNav from 'components/pages/navigation/sideNav/SideNav'
import TopBar from 'components/pages/navigation/topBar/TopBar'

import { ScrollContext } from './AppLayout'

/**
 * PlatformNavLayout — the NEW_PLATFORM_NAV shell (IC-48830, plan §7).
 *
 * Two-column layout:
 *   ┌──────────┬───────────────────────────┐
 *   │          │ [TopBar — sticky top]     │
 *   │ SideNav  ├───────────────────────────┤
 *   │ (rail)   │ [Content — scrollable]    │
 *   └──────────┴───────────────────────────┘
 *
 * - Left column = SideNav (full height, own scroll; Settings pinned via settingsSlot).
 * - Right column = TopBar (sticky) over the scrollable content.
 * - `--nav-width` is driven by the collapsed state (288px expanded / 60px collapsed).
 * - ScrollContext points at the CONTENT scroller only (preserving AppLayout's
 *   route-change scroll-reset behavior); the SideNav scrolls independently.
 * - Below 1050px the rail becomes an off-canvas overlay drawer (V3 Drawer)
 *   toggled by the TopBar hamburger (AC12).
 * - On /kpi the shell renders chrome-less (no rail / no top bar) — AC15.
 */

// Mirrors the old nav's 1050px mobile breakpoint (§6.1 / AC12).
const MOBILE_BREAKPOINT = 1050
const EXPANDED_WIDTH = 288
const COLLAPSED_WIDTH = 60
// Top bar height, published as --top-bar-height on the layout wrapper so TopBar
// and the rail's scrolled-state divider offset share one source of truth.
const TOP_BAR_HEIGHT = '52px'

// Per-user persistence key for the collapsed rail state (AC2).
export const collapsedStorageKey = (userId) => `ironscales-nav-collapsed:${userId ?? 'anon'}`

export const readCollapsed = (userId) => {
  // Product default (2026-08): the rail starts CLOSED. A first-time user with no
  // saved preference gets a collapsed rail; a returning user's explicit choice
  // (expanded OR collapsed) is always honoured.
  if (!LocalStorage.isAvailable) return true
  try {
    const stored = localStorage.getItem(collapsedStorageKey(userId))
    if (stored === null) return true
    return stored === 'true'
  } catch (e) {
    return true
  }
}

export const writeCollapsed = (userId, value) => {
  if (!LocalStorage.isAvailable) return
  try {
    localStorage.setItem(collapsedStorageKey(userId), value ? 'true' : 'false')
  } catch (e) {
    // ignore — persistence is best-effort
  }
}

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`

const BannerRow = styled.div`
  flex: 0 0 auto;
  width: 100%;
`

const LayoutWrapper = styled.div`
  display: grid;
  grid-template-columns: var(--nav-width) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  height: calc(100vh - var(--banner-height, 0px));
  min-height: 0;
  overflow: hidden;
  /* Animate the rail track in lockstep with the Rail's own width transition
     (SideNav, 0.18s ease). Without this the grid column snaps to the new
     nav-width instantly while the rail is still animating: NavColumn is
     overflow:hidden, so the rail gets clipped hard and the content edge jumps,
     and the collapse reads as a snap. Both endpoints keep the same track
     structure (a length + minmax(0,1fr)), so the px track interpolates cleanly. */
  transition: grid-template-columns 0.18s ease;

  &:has(#WizardControls) {
    #WizardControls,
    #side-nav-settings-slot {
      min-height: var(--t-spacing-16);
      box-sizing: border-box;
    }

    #side-nav-settings-slot {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding-block: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const NavColumn = styled.div`
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const ContentColumn = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  /* The page background lives HERE, not on ContentArea, so it also sits behind
     the sticky header band. The bar is transparent at rest (designer: "before
     scrolling - no color, transparent same way in dark mode"), and transparent
     reveals whatever is behind it — with the background one row lower, dark mode
     exposed the light <body> and the bar looked white. Painting once on the
     column also keeps the dark gradient continuous instead of restarting at the
     header/content boundary. */
  background: var(--color-background);
`

const HeaderArea = styled.header`
  position: sticky;
  top: 0;
  z-index: var(--z-index-navigation);
  display: flex;
  flex-direction: column;
`

const ContentArea = styled.main.attrs({ className: 'app-content platform-nav-content' })`
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  min-width: 0;
  /* Single source of truth for the page→chrome gap. Legacy pages historically
     hug the rail/top bar (each managed its own inset, so most had none and a
     handful over-padded), which read as inconsistent. Centralising it here means
     every page gets the same 20px side/bottom frame; top is 0 — the sticky
     top bar already separates chrome from content. Pages that carried their
     own outer inset are stripped below (see .platform-nav-content de-dupe
     rules) so they don't double up. */
  --page-inset: 20px;
  padding: 0 var(--page-inset) var(--page-inset);
  /* Background intentionally NOT set here — ContentColumn paints it so it also
     covers the sticky header band. Painting it in both places makes the dark
     theme's gradient restart at the header/content boundary and shows a seam. */
  background: transparent;
`

// Third ContentColumn grid row, a sibling of ContentArea rather than a
// descendant — outside the page inset entirely, so anything rendered into it
// spans the full content-column width without fighting --page-inset. Empty by
// default (0 height) so pages that don't use the footer slot are unaffected.
const FooterArea = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

// Context carrying the FooterArea DOM node once it's mounted. `null` (the
// default, and the value outside any PlatformNavLayout — old nav, or the
// chromeless /kpi branch) means "no slot": PlatformNavFooterSlot then renders
// its children inline instead of portaling, which is exactly what a page
// needs under old nav.
const PlatformNavFooterContext = React.createContext(null)

/**
 * Whether a PlatformNavLayout footer slot is available to portal into. A page
 * can use this to make its footer look identical everywhere it's rendered
 * under new nav (one visual style), while still choosing a different, e.g.
 * docked-in-a-card, look for old nav where no slot exists.
 */
export function usePlatformNavFooterSlot() {
  return useContext(PlatformNavFooterContext)
}

/**
 * Renders `children` into the platform nav's footer row (a real bottom bar,
 * full-bleed past the page inset) when rendered under PlatformNavLayout;
 * falls back to rendering them inline wherever it's mounted otherwise (old
 * nav, or no layout at all). Lets a page keep a single sticky-footer
 * component that adapts to whichever chrome it's rendered under, without
 * every new-nav page paying for an unused grid row.
 */
export function PlatformNavFooterSlot({ children }) {
  const node = usePlatformNavFooterSlot()
  return node ? createPortal(children, node) : children
}

// Chrome-less wrapper for /kpi: a single full-viewport scroll container.
const BareWrapper = styled.main.attrs({ className: 'app-content' })`
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  background: var(--color-background);
`

// The mobile drawer hosts the rail flush — drop the default Body padding so the
// SideNav fills the panel edge-to-edge.
const MobileNavDrawer = styled(Drawer)`
  .ds-v3-drawer__body {
    padding: 0;
  }
`

const PlatformNavLayout = ({ banner, children }) => {
  const scrollRef = useRef(null)
  const contentColumnRef = useRef(null)
  const headerRef = useRef(null)
  const bannerRef = useRef(null)
  const location = useLocation()
  const userId = useSelector(selectUserId)
  const windowWidth = useWindowWidth()

  const isMobile = windowWidth > 0 && windowWidth <= MOBILE_BREAKPOINT
  const isChromeless = location.pathname === URL_CONST.kpi.self

  // Whether the current route belongs to the Settings section. Derived from the
  // resolved nav's pinnedBottom (Settings) node `active` flag rather than a
  // "/settings" prefix, because several Settings items live elsewhere
  // (/activities, /resources, …) — a prefix check would drop
  // the panel and bounce the user to the main rail when they open those.
  const resolvedNav = useResolvedNav(undefined, location.pathname)
  const isSettingsRoute = Array.isArray(resolvedNav)
    ? !!resolvedNav.find((n) => n.pinnedBottom)?.active
    : false

  // Collapsed rail state, restored per user on load (AC2).
  const [collapsed, setCollapsed] = useState(() => readCollapsed(userId))
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // Settings sub-view ("extended settings mode"): while open the rail renders
  // at full width regardless of `collapsed`. Back returns to the expanded main
  // menu and saves that width, so a saved collapsed rail cannot hide it.
  // Opens ONLY by explicit intent — clicking the pinned Settings area (design
  // review 2026-08): reaching a settings page any other way (a Settings item that
  // lives under a MAIN section, e.g. ATO/Deepfake › Settings, or a deep link /
  // refresh) keeps the main rail. So it starts closed regardless of route.
  const [settingsOpen, setSettingsOpen] = useState(false)
  // TopBar scroll state (design review, 2026-07-27): the bar is transparent at
  // rest and only gains its solid fill + divider once the CONTENT scroller has
  // scrolled past the top — tracked here since this is the scroller's owner.
  const [contentScrolled, setContentScrolled] = useState(false)
  const lastScrolledRef = useRef(false)
  // Measured top-bar height (the banner lives in BannerRow, outside the header).
  // The rail retracts its right-edge divider by this so the connected band lands
  // on the real header rather than a fixed offset.
  const [headerHeight, setHeaderHeight] = useState(TOP_BAR_HEIGHT)
  const [bannerHeight, setBannerHeight] = useState('0px')
  const hasBanner = !!banner
  // FooterArea mounts after first render, so this starts null — children see
  // "no slot" for one render, then re-render once the node is set. Callback
  // ref (not useRef) so we re-render exactly when it changes.
  const [footerNode, setFooterNode] = useState(null)

  // Re-read persisted state once the user id resolves (it may be undefined on
  // the very first render before app data loads).
  useEffect(() => {
    setCollapsed(readCollapsed(userId))
  }, [userId])

  // Route-change scroll reset — preserves AppLayout's behavior on the CONTENT
  // scroller only.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0)
    }
    // A new page may not fire its own scroll event before the user scrolls
    // again — don't let a stale "scrolled" TopBar carry over from the last route.
    lastScrolledRef.current = false
    setContentScrolled(false)
  }, [location.pathname])

  // Close-only route sync (never auto-open — see settingsOpen note). Once the
  // active route no longer belongs to the Settings section, drop the sub-panel so
  // leaving Settings (logo→home, a highlight jump, etc.) returns to the main rail.
  // Opening stays exclusively user-driven via handleOpenSettings, so navigating to
  // a settings page from a main section never flips the rail into the settings menu.
  useEffect(() => {
    if (!isSettingsRoute) setSettingsOpen(false)
  }, [isSettingsRoute])

  // Profile menu (and any other explicit entry) can request the settings rail
  // via location.state. Dev already used openSettingsPanel; this branch also
  // sends openSettingsNav. Deep links / main-section Settings items stay closed.
  useEffect(() => {
    if ((location.state?.openSettingsNav || location.state?.openSettingsPanel) && isSettingsRoute) {
      setSettingsOpen(true)
    }
  }, [location.state, isSettingsRoute])

  // Track the page's real vertical scroller for the TopBar's scrolled state.
  // It's often NOT the layout's own ContentArea — many pages render an inner
  // full-height div (`.root`, their own `overflow-y: auto` container) that does
  // the actual scrolling while ContentArea stays at 0, so the listener sits on
  // the content column with capture:true (`scroll` doesn't bubble but does
  // capture). Only PAGE-LEVEL scrollers may drive the state: ContentArea
  // itself, or an inner scroller that fills (almost) the whole content area.
  // A fixed-height pane inside the page (e.g. the End-User Alerts template
  // list) previously flipped the state too — it is filtered out by the
  // height ratio below. Skipped in chromeless mode (no TopBar to drive there).
  useEffect(() => {
    if (isChromeless) return undefined
    const columnEl = contentColumnRef.current
    if (!columnEl) return undefined

    let rafId = null
    let pendingTarget = null

    const evaluate = () => {
      rafId = null
      const target = pendingTarget
      pendingTarget = null
      if (!target || typeof target.scrollTop !== 'number') return
      // Page-level scrollers only: ContentArea, or an inner container about as
      // tall as it. Anything meaningfully shorter is a widget inside the page.
      const pageEl = scrollRef.current
      if (pageEl && target !== pageEl && target.clientHeight < pageEl.clientHeight * 0.8) {
        return
      }
      // Guard against reacting to a barely-scrollable box — require a
      // meaningfully scrollable page.
      if (target.scrollHeight - target.clientHeight <= 40) return

      const next = target.scrollTop > 0
      if (next !== lastScrolledRef.current) {
        lastScrolledRef.current = next
        setContentScrolled(next)
      }
    }

    const handleScroll = (e) => {
      pendingTarget = e.target
      if (rafId == null) {
        rafId = window.requestAnimationFrame(evaluate)
      }
    }

    columnEl.addEventListener('scroll', handleScroll, { capture: true, passive: true })
    return () => {
      columnEl.removeEventListener('scroll', handleScroll, true)
      if (rafId != null) window.cancelAnimationFrame(rafId)
    }
  }, [/* effect dep */ isChromeless])

  // Track the sticky header's real height so the rail's scrolled-divider offset
  // follows it (banner appears/disappears, banner text wraps, window resizes).
  // Skipped in chromeless mode (no header rendered there).
  useEffect(() => {
    if (isChromeless) return undefined
    const headerEl = headerRef.current
    if (!headerEl) return undefined

    const measure = () => {
      const next = `${Math.round(headerEl.getBoundingClientRect().height)}px`
      setHeaderHeight((prev) => (prev === next ? prev : next))
    }
    measure()

    if (typeof ResizeObserver === 'undefined') return undefined
    // Observing HeaderArea catches every height change — banner show/hide, banner
    // text wrap, window resize — so this doesn't need `banner` as a dep (which
    // would just churn the observer on each parent render).
    const ro = new ResizeObserver(measure)
    ro.observe(headerEl)
    return () => ro.disconnect()
  }, [isChromeless])

  // The banner row sits above the whole layout, so the layout height must
  // shrink by its measured height (show/hide, text wrap, resize).
  useLayoutEffect(() => {
    const bannerEl = bannerRef.current
    if (isChromeless || !bannerEl) {
      setBannerHeight('0px')
      return undefined
    }

    const measure = () => {
      const next = `${Math.round(bannerEl.getBoundingClientRect().height)}px`
      setBannerHeight((prev) => (prev === next ? prev : next))
    }
    measure()

    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    ro.observe(bannerEl)
    return () => ro.disconnect()
  }, [isChromeless, hasBanner])

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  const handleToggleCollapsed = useCallback(() => {
    if (settingsOpen) return
    setCollapsed((prev) => {
      const next = !prev
      writeCollapsed(userId, next)
      return next
    })
  }, [userId, settingsOpen])

  const handleToggleMobileNav = useCallback(() => {
    setMobileNavOpen((prev) => !prev)
  }, [])

  const handleCloseMobileNav = useCallback(() => {
    setMobileNavOpen(false)
  }, [])

  const handleOpenSettings = useCallback(() => {
    setSettingsOpen(true)
  }, [])

  const handleCloseSettings = useCallback(() => {
    setSettingsOpen(false)
    if (isMobile) return
    setCollapsed(false)
    writeCollapsed(userId, false)
  }, [userId, isMobile])

  // /kpi — render no chrome (no rail, no top bar). Keep ScrollContext pointed
  // at the single bare scroll container so route-change reset still works (AC15).
  if (isChromeless) {
    return (
      <ScrollContext.Provider value={scrollRef}>
        {banner}
        <BareWrapper ref={scrollRef}>{children}</BareWrapper>
      </ScrollContext.Provider>
    )
  }

  // Desktop: the rail reflows via --nav-width. Mobile: rail width collapses to 0
  // and the SideNav lives inside the off-canvas Drawer instead. An open settings
  // sub-view forces the expanded width (a collapsed rail expands while it's open).
  const navWidth = isMobile
    ? '0px'
    : `${collapsed && !settingsOpen ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px`

  const sideNav = (
    <SideNav
      collapsed={isMobile ? false : collapsed}
      onToggleCollapsed={isMobile ? undefined : handleToggleCollapsed}
      collapseDisabled={settingsOpen}
      settingsSlot={
        <SettingsNav
          collapsed={isMobile ? false : collapsed}
          open={settingsOpen}
          onOpen={handleOpenSettings}
        />
      }
      settingsPanel={settingsOpen ? <SettingsPanelView onBack={handleCloseSettings} /> : null}
      // Once scrolled the bar adopts the rail's surface colour; the rail drops
      // its divider across that band so the two read as one piece (designer,
      // 2026-07-28: "the side and the top should look like 1 piece connected").
      scrolled={contentScrolled}
    />
  )

  return (
    <ScrollContext.Provider value={scrollRef}>
      {/* --top-bar-height is consumed by TopBar's own height and by the rail,
          which offsets its divider by exactly that much when scrolled. Declared
          once here so the two can never drift apart. */}
      <OuterWrapper style={{ '--banner-height': hasBanner ? bannerHeight : '0px' }}>
        {hasBanner && <BannerRow ref={bannerRef}>{banner}</BannerRow>}
        <LayoutWrapper
          style={{
            '--nav-width': navWidth,
            '--top-bar-height': TOP_BAR_HEIGHT,
            '--header-area-height': headerHeight,
          }}
        >
          {/* Persistent desktop rail; hidden (0 width) below the mobile breakpoint */}
          {!isMobile && <NavColumn>{sideNav}</NavColumn>}
          {isMobile && <NavColumn aria-hidden="true" />}

          <ContentColumn ref={contentColumnRef}>
            <HeaderArea ref={headerRef}>
              <TopBar
                onToggleMobileNav={handleToggleMobileNav}
                collapsed={collapsed && !settingsOpen}
                onToggleCollapsed={isMobile ? undefined : handleToggleCollapsed}
                collapseDisabled={settingsOpen}
                scrolled={contentScrolled}
              />
            </HeaderArea>
            <ContentArea ref={scrollRef}>
              <PlatformNavFooterContext.Provider value={footerNode}>
                {children}
              </PlatformNavFooterContext.Provider>
            </ContentArea>
            <FooterArea ref={setFooterNode} />
          </ContentColumn>
        </LayoutWrapper>
      </OuterWrapper>

      {/* Mobile off-canvas drawer — reuses V3 Drawer (AC12) */}
      {isMobile && (
        <MobileNavDrawer
          id="platform-nav-mobile-drawer"
          open={mobileNavOpen}
          onClose={handleCloseMobileNav}
          placement="left"
          width={EXPANDED_WIDTH}
          showCloseButton={false}
        >
          {sideNav}
        </MobileNavDrawer>
      )}
    </ScrollContext.Provider>
  )
}

export default PlatformNavLayout
